import React from 'react';
import PropTypes from 'prop-types';
import { Button, Icon } from 'semantic-ui-react';
import SelectablePlaylist from './SelectablePlaylist';
import UserPlaylist from '../../App/UserPlaylistQuery';
import Thumbnail from './Thumbnail';
import {
  Analytics,
  Drawer,
  GlobalNotifications,
  Intent,
  MobileModal
} from '../../Modules';
import { RootConsumer } from '../../RootComponent';
import pencilIcon from '../../../assets/pencil_icon.svg';
import styles from '../Playlist.scss';
import { graphql, commitMutation } from 'react-relay';

export default class MyPlaylists extends React.Component {
  static propTypes = {
    isSelected: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    parentID: PropTypes.string.isRequired,
    playlists: PropTypes.array.isRequired,
    togglePlaylist: PropTypes.func.isRequired,
    relay: PropTypes.object.isRequired,
    viewer: PropTypes.object.isRequired
  };

  constructor(props) {
    super(props);
    this.state = {
      activePlaylist: null,
      managePlaylists: false
    };
    this.getActionText = this.getActionText.bind(this);
    this.backAction = this.backAction.bind(this);
    this.createPlaylist = this.createPlaylist.bind(this);
    this.deletePlaylist = this.deletePlaylist.bind(this);
  }

  getActionText() {
    const { activePlaylist, managePlaylists } = this.state;
    return managePlaylists && !activePlaylist
      ? 'Back To Recommendations'
      : 'Done';
  }

  backAction() {
    const { activePlaylist, managePlaylists } = this.state;
    if (managePlaylists) {
      if (activePlaylist) {
        this.setState({ activePlaylist: null });
      } else {
        this.setState({ managePlaylists: false });
      }
    } else {
      this.props.onClose();
    }
  }

  createPlaylist(playlist, successCb, failureCb) {
    const onError = () => {
      if (failureCb) failureCb();
      GlobalNotifications.show({
        message: 'Unable to add playlist',
        intent: Intent.DANGER
      });
    };
    const onCompleted = response => {
      if (successCb) successCb();
      this.setState({
        activePlaylist: response.createPlaylist.new_playlist_edge.node.id
      });
      Analytics.trackEvent('created-playlist');
    };

    const mutation = graphql`
      mutation MyPlaylists_createPlaylistMutation(
        $input: CreatePlaylistInput!
      ) {
        createPlaylist(input: $input) {
          new_playlist_edge {
            node {
              id
              title
              description
              can_edit
              playlist_songs(first: 99999)
                @connection(key: "UserPlaylistContainer_playlist_songs") {
                edges {
                  node {
                    id
                  }
                }
              }
            }
          }
        }
      }
    `;

    const parentID = this.props.viewer.id;

    const configs = [
      {
        type: 'RANGE_ADD',
        parentID,
        edgeName: 'new_playlist_edge',
        connectionInfo: [
          {
            key: 'PlaylistsContainer_playlists',
            rangeBehavior: 'append'
          },
          {
            key: 'PlannerPlaylistContainer_playlists',
            rangeBehavior: 'append'
          },
          {
            key: 'MpTemplateContainer_playlists',
            rangeBehavior: 'append'
          }
        ]
      }
    ];

    const environment = this.props.relay.environment;

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          title: '(Untitled Playlist)'
        }
      },
      configs,
      onCompleted,
      onError
    });
  }

  deletePlaylist() {
    const onError = () => {
      GlobalNotifications.show({
        message: 'Unable to delete playlist',
        intent: Intent.DANGER
      });
    };

    const onCompleted = () => {
      this.setState({ activePlaylist: null });
      Analytics.trackEvent('deleted-playlist');
    };

    const mutation = graphql`
      mutation MyPlaylists_deletePlaylistMutation(
        $input: DeletePlaylistInput!
      ) {
        deletePlaylist(input: $input) {
          deletedPlaylistId
        }
      }
    `;

    const environment = this.props.relay.environment;

    const configs = [
      {
        type: 'RANGE_DELETE',
        parentID: this.props.viewer.id,
        pathToConnection: ['viewer', 'playlists'],
        deletedIDFieldName: 'deletedPlaylistId',
        connectionKeys: [
          {
            key: 'PlaylistsContainer_playlists',
            rangeBehavior: 'append'
          },
          {
            key: 'PlannerPlaylistContainer_playlists',
            rangeBehavior: 'append'
          },
          {
            key: 'MpTemplateContainer_playlists',
            rangeBehavior: 'append'
          }
        ]
      }
    ];

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          id: this.state.activePlaylist
        }
      },
      configs,
      onCompleted,
      onError
    });
  }

  render() {
    const {
      isSelected,
      parentID,
      playlists,
      relay,
      togglePlaylist
    } = this.props;
    const { activePlaylist, managePlaylists } = this.state;
    return (
      <RootConsumer>
        {({ isMobile }) =>
          <React.Fragment>
            {isMobile &&
              <MobileModal.Header>
                <div className='menu'>
                  <a className='item' onClick={this.backAction}>
                    <Icon name='chevron left' />
                  </a>
                </div>
              </MobileModal.Header>}
            <Drawer.Content>
              {!managePlaylists &&
                playlists.map(({ node: playlist }) =>
                  <SelectablePlaylist
                    playlist={playlist}
                    key={playlist.id}
                    togglePlaylist={togglePlaylist}
                    isSelected={isSelected}
                  />
                )}
              {managePlaylists &&
                !activePlaylist &&
                playlists.map(({ node: playlist }) =>
                  <Thumbnail
                    key={playlist.id}
                    playlist={playlist}
                    onClick={() =>
                      this.setState({ activePlaylist: playlist.id })}
                    actionIcon={pencilIcon}
                    action={() =>
                      this.setState({ activePlaylist: playlist.id })}
                    actionClassName={styles.toggleRecommendation}
                  />
                )}
              {managePlaylists &&
                activePlaylist &&
                <UserPlaylist
                  playlistId={activePlaylist}
                  relay={relay}
                  parentID={parentID}
                  showControls
                  deletePlaylist={this.deletePlaylist}
                  goBack={() => this.setState({ activePlaylist: null })}
                />}
              {managePlaylists &&
                !activePlaylist &&
                playlists.length > 0 &&
                <p style={{ marginTop: '1em' }}>
                  <a onClick={this.createPlaylist}>+ Add Playlist</a>
                </p>}
              {playlists.length === 0 &&
                <div style={{ padding: '2em', textAlign: 'center' }}>
                  <h4>You have no playlists</h4>
                  <p>
                    You have no playlists saved to your account.<br />
                    Get started by creating your first playlist.
                  </p>
                  <Button
                    primary
                    icon='plus'
                    onClick={() => {
                      this.setState({ managePlaylists: true });
                      this.createPlaylist();
                    }}
                    labelPosition='left'
                    content='Create Playlist'
                  />
                </div>}
            </Drawer.Content>
            <Drawer.Footer>
              {!managePlaylists &&
                <Button
                  icon='pencil'
                  basic
                  content='Edit Playlists'
                  onClick={() => this.setState({ managePlaylists: true })}
                  style={{ marginRight: '1em' }}
                />}
              <Button
                onClick={this.backAction}
                primary
                content={this.getActionText()}
              />
            </Drawer.Footer>
          </React.Fragment>}
      </RootConsumer>
    );
  }
}



// WEBPACK FOOTER //
// ./webpack/components/Playlist/Recommendation/MyPlaylists.js